import React, { useState } from 'react';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import Tab from 'housecall-ui/dist/DesignSystem/Navigation/Tabs/Tab';
import Tabs from 'housecall-ui/dist/DesignSystem/Navigation/Tabs/Tabs';
import HousecallThemeProvider from 'housecall-ui/dist/DesignSystem/Styles/HousecallThemeProvider';
import ExpandMoreIcon from 'housecall-ui/dist/IconsAndImages/Icons/ExpandMore';
import SettingsIcon from 'housecall-ui/dist/IconsAndImages/Icons/Settings';
import IconButton from 'housecall-ui/dist/library/Buttons/IconButton';
import { QueryClientProvider } from 'resources/ReactQuery/QueryClientProvider';

import RedirectTo from 'components/Common/RedirectTo';
import GlobalSearchContainer from 'components/Toolbar/v2/GlobalSearch/GlobalSearchContainer';

import { NavProps, NavTab } from '../types';

import AddButton from './AddButton';
import AvatarMenu from './AvatarMenu';
import NavPopover from './NavPopover';
import TabLink from './TabLink';
import LabelWithBadge from './LabelWithBadge';

const TabletNav = ({ onTabChange, activeTab, tabs = [] }: NavProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const MORE_TAB_INDEX = 3;

  const handleTabChange = (event: React.ChangeEvent<unknown>, tabIndex: number) => {
    if (tabIndex === MORE_TAB_INDEX) {
      return onTabChange(event, activeTab);
    }

    return onTabChange(event, tabIndex);
  };

  const handleMoreMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setAnchorEl(null);
  };

  const isMoreMenuOpen = Boolean(anchorEl);
  const id = isMoreMenuOpen ? 'nav-more-menu' : undefined;

  const navTabs = React.useMemo(
    () => (): { first: NavTab[]; second: NavTab[] | [] } => {
      if (tabs.length <= MORE_TAB_INDEX) return { first: tabs, second: [] };

      return {
        first: tabs.slice(0, MORE_TAB_INDEX),
        second: tabs.slice(MORE_TAB_INDEX),
      };
    },
    [tabs]
  );

  const { first: visibleTabs, second: moreMenuTabs } = navTabs();

  return (
    <Grid data-testid="TabletNav-root" container alignItems="center" spacing={1} wrap="nowrap">
      <Grid item xs={6}>
        <Tabs version="v2" onChange={handleTabChange} value={activeTab}>
          {visibleTabs.map(({ label, path, badgeValue: badgeCount }) => (
            <TabLink
              key={label}
              version="v2"
              href={path}
              label={<LabelWithBadge badgeCount={badgeCount}>{label}</LabelWithBadge>}
              tabIndex={0}
            />
          ))}
          {moreMenuTabs.length > 0 && [
            <Tab
              key="more"
              version="v2"
              label="More"
              onClick={handleMoreMenuClick}
              secondary={<ExpandMoreIcon />}
              tabIndex={0}
              aria-describedby={id}
            />,
            <NavPopover
              key={id}
              id={id}
              anchorEl={anchorEl}
              open={isMoreMenuOpen}
              onClose={handleMoreMenuClose}
              navItems={moreMenuTabs}
            />,
          ]}
        </Tabs>
      </Grid>
      <Grid container item xs={6} spacing={1} alignItems="center" justify="flex-end" wrap="nowrap">
        <Grid item>
          <HousecallThemeProvider mode="light">
            <QueryClientProvider>
              <GlobalSearchContainer responsive />
            </QueryClientProvider>
          </HousecallThemeProvider>
        </Grid>
        <Grid item>
          <AddButton variant="button" />
        </Grid>
        <Grid item>
          <IconButton aria-label="Settings" component="a" onClick={() => RedirectTo.accountSettings()}>
            <SettingsIcon />
          </IconButton>
        </Grid>
        <Grid item>
          <AvatarMenu />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TabletNav;
